// Shown in place of a value that isn't available (yet).
export const EMPTY_VALUE = "-";

export const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

// Intl.NumberFormat instances are costly to build, and toLocaleString(locale, options) builds one per
// call - cached here per locale and options, for formatting done on every render.
const numberFormatCache = new Map<string, Intl.NumberFormat>();

export const getNumberFormat = (
  locale: string | undefined,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat => {
  const key = `${locale ?? ""}|${JSON.stringify(options)}`;
  let numberFormat = numberFormatCache.get(key);

  if (!numberFormat) {
    numberFormat = new Intl.NumberFormat(locale, options);
    numberFormatCache.set(key, numberFormat);
  }

  return numberFormat;
};

// The value with at most `decimals` fraction digits, or EMPTY_VALUE when it isn't a number.
export const formatNumber = (locale: string | undefined, value: number | null | undefined, decimals = 0): string =>
  isFiniteNumber(value) ? getNumberFormat(locale, { maximumFractionDigits: decimals }).format(value) : EMPTY_VALUE;

// Same as formatNumber, followed by its unit.
export const formatQuantity = (
  locale: string | undefined,
  value: number | null | undefined,
  unit: string,
  decimals = 0,
): string => (isFiniteNumber(value) ? `${formatNumber(locale, value, decimals)} ${unit}` : EMPTY_VALUE);
